import { Close, DeleteOutline, ImageOutlined, PlaylistAdd, TableChart, Videocam } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box, Chip, Divider, Drawer, FormControl, IconButton, InputLabel,
  MenuItem, Select, SelectChangeEvent, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Media } from '../../Model/MediaModel'
import { Playlist, PlaylistItems } from '../../Model/PlaylistModel'
import { useBroadcastPlaylistUpdateMutation } from '../../service/DeviceSessionService'
import { toPlaylistDTO, useUpdatePlaylistMutation } from '../../service/PlaylistService'
import { useAppSelector } from '../../store/hooks'
import config from '../../Util/config'
import { resolveMediaUrl } from '../../Util/mediaUrl'
import { mediaToPlaylistItem } from '../../Util/playlistItem'

const MAX_ITEMS = config.FEATURE.MAX_PLAYLIST_ITEMS

type Props = {
  open: boolean
  onClose: () => void
  /** Mídias selecionadas na grade, candidatas a inclusão. */
  newMedias: Media[]
  /** Chamado após salvar com sucesso (ex.: sair do modo seleção). */
  onSaved?: () => void
}

function itemLabel(it: PlaylistItems): string {
  if (it.type === 'priceTable') return (it.data as any)?.name ?? 'Tabela de preço'
  return (it.data as Media)?.desc ?? 'Mídia'
}

const PlaylistQuickPanel = ({ open, onClose, newMedias, onSaved }: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const playlists = useAppSelector(s => s.playlist)

  const [updatePlaylist, { isLoading: isSaving }] = useUpdatePlaylistMutation()
  const [broadcast] = useBroadcastPlaylistUpdateMutation()

  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [items, setItems] = useState<PlaylistItems[]>([])
  const [staged, setStaged] = useState<Media[]>([])
  const [insertIndex, setInsertIndex] = useState(0)

  const selectedPlaylist = playlists.find(p => p.id === selectedId)

  // Ao (re)abrir, recarrega a lista de mídias candidatas vindas da seleção.
  useEffect(() => {
    if (open) setStaged(newMedias)
  }, [open, newMedias])

  // Ao escolher uma playlist, carrega seus itens e posiciona a inserção no fim.
  // Depende só de selectedId de propósito: não re-sincroniza (e não apaga as
  // remoções locais) se a lista de playlists do Redux for atualizada por baixo.
  useEffect(() => {
    const pl = playlists.find(p => p.id === selectedId)
    if (!pl) { setItems([]); setInsertIndex(0); return }
    const ordered = [...pl.items].sort((a, b) => a.order - b.order)
    setItems(ordered)
    setInsertIndex(ordered.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  const remaining = Math.max(0, MAX_ITEMS - items.length)
  const includedStaged = staged.slice(0, remaining)
  const droppedCount = staged.length - includedStaged.length
  const total = items.length + includedStaged.length
  const showSlots = includedStaged.length > 0

  function handleClose() {
    setSelectedId('')
    setItems([])
    setStaged([])
    setInsertIndex(0)
    onClose()
  }

  function removeItem(idx: number) {
    const next = items.filter((_, i) => i !== idx)
    setItems(next)
    setInsertIndex(ii => (ii > idx ? ii - 1 : Math.min(ii, next.length)))
  }

  function removeStaged(id: number) {
    setStaged(prev => prev.filter(m => m.id !== id))
  }

  async function handleSave() {
    if (!selectedPlaylist) return

    const newItems = includedStaged.map(m => mediaToPlaylistItem(m, 0))
    const merged: PlaylistItems[] = [
      ...items.slice(0, insertIndex),
      ...newItems,
      ...items.slice(insertIndex),
    ].map((it, i) => ({ ...it, order: i + 1 }))

    const entity: Playlist = { ...selectedPlaylist, items: merged }

    try {
      await updatePlaylist({ playlist: toPlaylistDTO(entity) }).unwrap()
    } catch {
      // O service já exibe o toast de erro específico.
      return
    }

    if (droppedCount > 0) {
      toast.info(`${includedStaged.length} adicionada(s); ${droppedCount} ignorada(s) — limite de ${MAX_ITEMS} itens`)
    }

    const pid = selectedPlaylist.id
    if (pid) {
      try {
        await toast.promise(broadcast(pid.toString()).unwrap(), {
          pending: 'Enviando para a TV...',
          success: 'Enviado para a TV ✓',
          error: 'Salvo, mas FALHOU o envio para a TV. Tente salvar de novo.',
        })
      } catch {
        // toast.promise já mostrou o erro; mantém o painel aberto não é útil aqui — segue.
      }
    }

    onSaved?.()
    handleClose()
  }

  // ── Linha de ponto de inserção (Opção A) ──
  const InsertSlot = ({ index }: { index: number }) => {
    if (!showSlots) return null
    const active = insertIndex === index
    return (
      <Box
        onClick={() => setInsertIndex(index)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.25,
          cursor: 'pointer', userSelect: 'none',
          '&:hover .ins-line': { bgcolor: active ? 'primary.main' : 'primary.light' },
        }}
      >
        <Box className="ins-line" sx={{ flex: 1, height: active ? 2 : 1, borderRadius: 1, bgcolor: active ? 'primary.main' : 'divider', transition: 'all .15s' }} />
        <Typography variant="caption" sx={{ color: active ? 'primary.main' : 'text.disabled', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }}>
          {active ? '▸ inserir aqui' : 'inserir aqui'}
        </Typography>
        <Box className="ins-line" sx={{ flex: 1, height: active ? 2 : 1, borderRadius: 1, bgcolor: active ? 'primary.main' : 'divider', transition: 'all .15s' }} />
      </Box>
    )
  }

  // ── Miniatura de um item ──
  const Thumb = ({ it }: { it: PlaylistItems }) => {
    if (it.type === 'priceTable') {
      return (
        <Box sx={{ width: 44, height: 44, borderRadius: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
          <TableChart sx={{ fontSize: 20, color: 'text.disabled' }} />
        </Box>
      )
    }
    const media = it.data as Media
    const src = resolveMediaUrl(media?.urlPreview || media?.url)
    return (
      <Box sx={{ width: 44, height: 44, borderRadius: 1, flexShrink: 0, overflow: 'hidden', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {src
          ? <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (it.type === 'video' ? <Videocam sx={{ fontSize: 20, color: 'text.disabled' }} /> : <ImageOutlined sx={{ fontSize: 20, color: 'text.disabled' }} />)}
      </Box>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: isMobile ? '100%' : 440, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <PlaylistAdd sx={{ color: 'primary.main' }} />
        <Typography fontWeight={600} sx={{ flex: 1 }}>Gerenciar playlist</Typography>
        <IconButton size="small" onClick={handleClose}><Close fontSize="small" /></IconButton>
      </Box>

      {/* Seletor de playlist */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1, flexShrink: 0 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Escolha uma playlist</InputLabel>
          <Select
            label="Escolha uma playlist"
            value={selectedId}
            onChange={(e: SelectChangeEvent<number | ''>) => setSelectedId(e.target.value as number)}
          >
            {playlists.map(p => (
              <MenuItem key={p.id} value={p.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Typography variant="body2" noWrap>{p.name}</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                    {p.items.length}/{MAX_ITEMS}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Corpo scrollável */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 1 }}>
        {!selectedPlaylist ? (
          <Box sx={{ textAlign: 'center', color: 'text.disabled', mt: 6 }}>
            <PlaylistAdd sx={{ fontSize: 40, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>Selecione uma playlist para gerenciar.</Typography>
          </Box>
        ) : (
          <>
            {/* Novas mídias (staging) */}
            {staged.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Novas mídias ({includedStaged.length})
                </Typography>
                {droppedCount > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
                    {droppedCount} não cabe(m): playlist atinge o limite de {MAX_ITEMS}.
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {staged.map((m, i) => {
                    const willInclude = i < remaining
                    return (
                      <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1.5, border: 1, borderColor: 'divider', opacity: willInclude ? 1 : 0.45 }}>
                        <Thumb it={mediaToPlaylistItem(m, 0)} />
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>{m.desc}</Typography>
                        <Chip label="nova" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                        <Tooltip title="Não incluir">
                          <IconButton size="small" onClick={() => removeStaged(m.id)}>
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  })}
                </Box>
                {showSlots && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                    Toque em “inserir aqui” abaixo para escolher a posição.
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Playlist atual */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="text.secondary">Playlist atual</Typography>
              <Chip label={`${total}/${MAX_ITEMS}`} size="small" color={total >= MAX_ITEMS ? 'warning' : 'default'} sx={{ height: 20, fontSize: 11 }} />
            </Box>

            {items.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>Playlist vazia.</Typography>
            ) : (
              <Box>
                <InsertSlot index={0} />
                {items.map((it, idx) => (
                  <Box key={it.id ?? `i${idx}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.disabled" sx={{ width: 18, textAlign: 'right', flexShrink: 0 }}>{idx + 1}</Typography>
                      <Thumb it={it} />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>{itemLabel(it)}</Typography>
                      <Tooltip title="Remover da playlist">
                        <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                          <DeleteOutline sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <InsertSlot index={idx + 1} />
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 1.75, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
        <LoadingButton
          fullWidth variant="contained" color="success" loading={isSaving}
          disabled={!selectedPlaylist}
          onClick={handleSave}
        >
          Salvar e enviar à TV
        </LoadingButton>
      </Box>
    </Drawer>
  )
}

export default PlaylistQuickPanel
